import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ShieldAlert, MailCheck, RotateCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_LABEL = {
  add_main_admin:   'Add main admin',
  delete_main_admin: 'Remove main admin',
  launch_reset:     'App-wide reset',
};

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 20,
  border: '1px solid #E5E7EB',
  padding: 28,
  display: 'grid',
  gap: 16,
  textAlign: 'center',
  boxShadow: '0 10px 30px rgba(15,17,21,0.06)',
};

export default function AdminActionConfirmPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token) {
      setState({ status: 'error', detail: 'Missing confirmation token.' });
      return;
    }
    (async () => {
      try {
        const res = await axios.post(`${API}/admin/actions/confirm`, { token });
        setState({ status: 'ok', payload: res.data });
      } catch (err) {
        setState({ status: 'error', detail: err?.response?.data?.detail || 'Could not confirm.' });
      }
    })();
  }, [location.search]);

  if (state.status === 'loading') {
    return (
      <div className="admin-scope" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22, background: '#F6F7F9' }}>
        <div style={cardStyle} data-testid="admin-action-confirm-loading">
          <RotateCw size={28} color="#0F1115" className="rotate" />
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18 }}>Confirming…</p>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="admin-scope" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22, background: '#F6F7F9' }}>
        <div style={cardStyle} data-testid="admin-action-confirm-error">
          <ShieldAlert size={36} color="#DD0608" style={{ margin: '0 auto' }} />
          <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, margin: 0 }}>Confirmation failed</h2>
          <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>{state.detail}</p>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            data-testid="admin-action-confirm-back"
            style={{ padding: '10px 16px', borderRadius: 9999, border: 'none', background: '#0F1115', color: '#FFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            Back to admin
          </button>
        </div>
      </div>
    );
  }

  const payload = state.payload || {};
  const actionLabel = ACTION_LABEL[payload.action_type] || 'Action';
  const isExecuted = payload.status === 'executed';
  const isAlready = payload.already_finalised;

  return (
    <div className="admin-scope" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22, background: '#F6F7F9' }}>
      <div style={cardStyle} data-testid="admin-action-confirm-success">
        {isExecuted ? (
          <ShieldCheck size={36} color="#0F6B19" style={{ margin: '0 auto' }} />
        ) : (
          <MailCheck size={36} color="#0F1115" style={{ margin: '0 auto' }} />
        )}
        <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, margin: 0 }}>
          {isAlready ? 'Already finalised' : isExecuted ? `${actionLabel} confirmed` : 'Confirmation recorded'}
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>
          {isAlready
            ? 'This action was already completed earlier — nothing more to do.'
            : isExecuted
              ? `Action “${actionLabel}” has executed successfully.`
              : `${payload.received || 1} of ${payload.required || '?'} required confirmations recorded. Waiting on the rest.`}
        </p>
        <button
          type="button"
          onClick={() => navigate('/admin')}
          data-testid="admin-action-confirm-back"
          style={{ padding: '10px 16px', borderRadius: 9999, border: 'none', background: '#0F1115', color: '#FFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
        >
          Back to admin
        </button>
      </div>
    </div>
  );
}
